<template>
  <div class="card mb-3 mb-md-4 gasto-form-card">
    <div class="card-body p-2 p-md-3">
      <h4 class="gasto-form-title">Nuevo Gasto</h4>
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">Fecha</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="fecha" 
              required
            >
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Concepto</label>
            <div class="position-relative">
              <input 
                type="text" 
                class="form-control" 
                v-model="concepto" 
                @input="searchConceptos"
                @focus="showSuggestions = true"
                @blur="hideSuggestionsDelayed"
                required
              >
              <!-- Lista de sugerencias -->
              <div 
                v-if="showSuggestions && filteredConceptos.length" 
                class="suggestions-list"
              >
                <div 
                  v-for="sugerencia in filteredConceptos" 
                  :key="sugerencia.concepto"
                  class="suggestion-item"
                  @mousedown.prevent="selectConcepto(sugerencia)"
                >
                  <div class="d-flex justify-content-between w-100">
                    <div>{{ sugerencia.concepto }}</div>
                    <div class="text-end">
                      <small class="text-muted">{{ sugerencia.categoria_nombre }}</small>
                      <small class="text-muted ms-2">({{ sugerencia.tipo_division }})</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Categoría</label>
            <select 
              class="form-select" 
              v-model="categoria_id" 
              required
            >
              <option value="">Selecciona una categoría</option>
              <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                {{ cat.nombre }}
              </option>
            </select>
          </div>
          
          <div class="col-12 mb-3">
            <label class="form-label">Cantidad</label>
            <input 
              type="number" 
              step="0.01" 
              class="form-control" 
              v-model="cantidad" 
              @input="calcularCantidadTotal"
              required
            >
          </div>
          <div class="col-12 mb-3" v-if="!tieneGrupo">
            <label class="form-label">División</label>
            <select class="form-select" v-model="tipoDivision" @change="calcularCantidadTotal">
              <option value="total">Total</option>
              <option value="mitad">Mitad</option>
            </select>
          </div>
          <div class="col-12 mb-3" v-if="tieneGrupo">
            <label class="form-label">Compartir con el grupo</label>
            <div v-if="!companeros.length" class="form-text">
              Todavía no hay más miembros. Invítalos desde
              <router-link to="/grupo">Grupo</router-link>.
              El gasto se guardará íntegro para ti.
            </div>
            <div v-else class="border rounded p-2">
              <div class="form-check" v-for="p in companeros" :key="p.id">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'comp-' + p.id"
                  :value="p.id"
                  v-model="participantes"
                >
                <label class="form-check-label" :for="'comp-' + p.id">
                  {{ p.nombre }}
                  <small class="text-muted">({{ p.email }})</small>
                </label>
              </div>
            </div>
            <div class="form-text" v-if="companeros.length">
              Se divide a partes iguales entre tú y las personas marcadas.
            </div>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">{{ tieneGrupo ? 'Tu parte' : 'Cantidad Total' }}</label>
            <div class="form-control bg-light">
              {{ cantidadTotalMostrar }}
              <small v-if="tieneGrupo && participantes.length" class="text-muted">
                ({{ participantes.length + 1 }} personas)
              </small>
            </div>
          </div>
        </div>
        <button type="submit" class="btn btn-primary">
          Guardar Gasto
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';

export default {
  name: 'GastoForm',
  emits: ['gasto-added'],
  
  setup(props, { emit }) {
    const store = useStore();
    const concepto = ref('');
    const cantidad = ref('');
    const categoria_id = ref('');
    const fecha = ref(new Date().toISOString().split('T')[0]);
    const categorias = ref([]);
    const conceptosGuardados = ref([]);
    const showSuggestions = ref(false);
    const tipoDivision = ref('total');
    const tieneGrupo = ref(false);
    const companeros = ref([]);
    const participantes = ref([]);
    
    // Modificar el computed para manejar valores undefined
    const filteredConceptos = computed(() => {
      if (!concepto.value || !conceptosGuardados.value) return [];
      const searchTerm = concepto.value.toLowerCase();
      console.log('Conceptos guardados:', conceptosGuardados.value);
      return conceptosGuardados.value
        .filter(c => c && c.concepto && c.concepto.toLowerCase().includes(searchTerm))
        .slice(0, 5);
    });

    const cantidadTotalMostrar = computed(() => {
      void tieneGrupo.value;
      void participantes.value;
      void tipoDivision.value;
      const total = calcularCantidadTotal();
      return total ? `${total.toFixed(2)} €` : '0.00 €';
    });

    const loadCategorias = async () => {
      try {
        const response = await axios.get('/api/categorias');
        categorias.value = response.data;
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };

    const loadCompaneros = async () => {
      try {
        const response = await axios.get('/api/grupos/companeros');
        tieneGrupo.value = !!response.data.tiene_grupo;
        companeros.value = response.data.companeros || [];
        if (tieneGrupo.value) {
          tipoDivision.value = 'total';
        }
      } catch (error) {
        console.error('Error al cargar compañeros de grupo:', error);
        tieneGrupo.value = false;
        companeros.value = [];
      }
    };

    const loadConceptos = async () => {
      try {
        const response = await axios.get('/api/gastos/conceptos');
        console.log('Conceptos cargados:', response.data);
        conceptosGuardados.value = response.data || [];
      } catch (error) {
        console.error('Error al cargar conceptos:', error);
        conceptosGuardados.value = [];
      }
    };

    const selectConcepto = (selectedSuggestion) => {
      console.log('Sugerencia seleccionada:', selectedSuggestion);
      concepto.value = selectedSuggestion.concepto;
      // Autocompletar la categoría y división si existen
      if (selectedSuggestion.categoria_id) {
        categoria_id.value = selectedSuggestion.categoria_id.toString();
      }
      if (selectedSuggestion.tipo_division && !tieneGrupo.value) {
        tipoDivision.value = selectedSuggestion.tipo_division;
      }
      showSuggestions.value = false;
    };

    const hideSuggestionsDelayed = () => {
      setTimeout(() => {
        showSuggestions.value = false;
      }, 200);
    };

    const searchConceptos = () => {
      showSuggestions.value = true;
    };

    const calcularCantidadTotal = () => {
      if (!cantidad.value) return 0;
      const cantidadNum = parseFloat(cantidad.value);
      if (isNaN(cantidadNum)) return 0;

      if (tieneGrupo.value) {
        const n = 1 + (participantes.value || []).length;
        return Math.round((cantidadNum / n) * 100) / 100;
      }
      if (tipoDivision.value === 'mitad') {
        return cantidadNum / 2;
      }
      return cantidadNum;
    };

    const handleSubmit = async () => {
      try {
        // Validar que todos los campos estén completos
        if (!concepto.value.trim()) {
          alert('El concepto es obligatorio');
          return;
        }

        if (!cantidad.value || parseFloat(cantidad.value) <= 0) {
          alert('La cantidad debe ser mayor que 0');
          return;
        }

        if (!categoria_id.value) {
          alert('Debe seleccionar una categoría');
          return;
        }

        console.log('Enviando datos del nuevo gasto al servidor...');
        
        // Calcular la cantidad total antes de enviar
        const cantidadTotal = calcularCantidadTotal();
        const datosGasto = {
          concepto: concepto.value.trim(),
          cantidad: parseFloat(cantidad.value),
          cantidad_total: cantidadTotal,
          tipo_division: tieneGrupo.value
            ? (participantes.value.length ? 'grupo' : 'total')
            : tipoDivision.value,
          categoria_id: categoria_id.value,
          fecha: fecha.value,
          participantes: tieneGrupo.value ? participantes.value : []
        };
        
        console.log('Datos del gasto a enviar:', datosGasto);
        
        // Enviar datos al servidor
        const response = await axios.post('/api/gastos', datosGasto);
        
        console.log('Respuesta completa del servidor:', response);
        console.log('Gasto guardado correctamente:', response.data);
        console.log('Tipo de response.data:', typeof response.data);
        console.log('¿response.data es un objeto?', typeof response.data === 'object');
        console.log('Propiedades de response.data:', Object.keys(response.data));
        
        // Asegurarse de que el ID del gasto se guarde correctamente
        if (response.data && response.data.id) {
          console.log('ID del gasto creado:', response.data.id);
          console.log('Tipo del ID:', typeof response.data.id);
          
          // Verificar si el ID es un número o una cadena y convertirlo si es necesario
          const gastoId = typeof response.data.id === 'string' ? parseInt(response.data.id, 10) : response.data.id;
          
          // Crear una copia del objeto para asegurarnos de que el ID es un número
          const gastoConId = {
            ...response.data,
            id: gastoId
          };
          
          console.log('Gasto con ID verificado:', gastoConId);
          
          // Limpiar formulario
          concepto.value = '';
          cantidad.value = '';
          tipoDivision.value = 'total';
          categoria_id.value = '';
          fecha.value = new Date().toISOString().split('T')[0];
          participantes.value = [];
          
          // Notificar al componente padre con el objeto que tiene el ID verificado
          console.log('Emitiendo evento gasto-added con ID verificado');
          emit('gasto-added', gastoConId);
        } else {
          console.warn('No se recibió un ID válido del servidor:', response.data);
          alert('Error: No se pudo obtener el ID del gasto creado. Por favor, intente nuevamente.');
        }
        
        // Recargar conceptos para incluir el nuevo
        loadConceptos();
      } catch (error) {
        console.error('Error al guardar el gasto:', error);
        
        // Mostrar información detallada del error
        if (error.response) {
          // La petición fue realizada y el servidor respondió con un código de estado
          // que no está en el rango 2xx
          console.error('Datos de respuesta de error:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers
          });
          
          alert(`Error al guardar el gasto: ${error.response.status} - ${error.response.data.error || error.response.statusText}`);
        } else if (error.request) {
          // La petición fue realizada pero no se recibió respuesta
          console.error('No se recibió respuesta del servidor:', error.request);
          alert('Error al guardar el gasto: No se recibió respuesta del servidor');
        } else {
          // Algo ocurrió al configurar la petición que desencadenó un error
          console.error('Error al configurar la petición:', error.message);
          alert(`Error al guardar el gasto: ${error.message}`);
        }
      }
    };

    onMounted(() => {
      loadCategorias();
      loadConceptos();
      loadCompaneros();
    });
    
    return {
      concepto,
      cantidad,
      categoria_id,
      fecha,
      categorias,
      conceptosGuardados,
      filteredConceptos,
      showSuggestions,
      handleSubmit,
      searchConceptos,
      selectConcepto,
      hideSuggestionsDelayed,
      tipoDivision,
      tieneGrupo,
      companeros,
      participantes,
      calcularCantidadTotal,
      cantidadTotalMostrar
    };
  }
};
</script>

<style scoped>
.gasto-form-title {
  font-size: 1.1rem;
}

@media (min-width: 768px) {
  .gasto-form-title {
    font-size: 1.25rem;
  }
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-item small {
  font-size: 0.85em;
}
</style> 